import { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../../../firebaseConfig';
import { getUserData } from '../../../../../pages/auth/utils/firebase/userOperations';
import { getDisplayName } from '../../../../../utils/userDisplay';
import notificationActionHandler from '../../../../notifications/services/notificationActionHandler';
import {
    markAsRead as markAsReadAction,
    updateNotification
} from '../../../../notifications/store/notificationsSlice';
import { updateUserData as updateUserDataLocal } from '../../../../../redux/slices/authSlice';
import { getDateGroup } from '../../../../../utils/dateFormat';

export const useNotificationPageData = ({
    currentUser,
    authUserData,
    filteredNotifications,
    navigate,
    markAsRead,
    deleteNotification
}) => {
    const dispatch = useDispatch();
    const [invites, setInvites] = useState([]);
    const [userCache, setUserCache] = useState({});
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [processingIds, setProcessingIds] = useState(() => new Set());
    const [openMenuId, setOpenMenuId] = useState(null);

    // Legacy project invites
    useEffect(() => {
        if (!currentUser?.uid) return;
        const loadInvites = async () => {
            try {
                const invitesQ = query(
                    collection(db, 'project_invites'),
                    where('inviteeId', '==', currentUser.uid),
                    where('status', '==', 'pending')
                );
                const snap = await getDocs(invitesQ);
                const list = snap.docs.map(d => ({ id: d.id, ...d.data(), _isInvite: true }));
                setInvites(list);
            } catch (error) {
                console.error('Failed to load project invites:', error);
            }
        };
        loadInvites();
    }, [currentUser?.uid]);

    // Load actor profiles
    useEffect(() => {
        const ids = new Set();
        filteredNotifications.forEach(n => n.actorId && ids.add(n.actorId));
        invites.forEach(i => i.inviterId && ids.add(i.inviterId));

        const missing = Array.from(ids).filter(id => !userCache[id]);
        if (missing.length === 0) return;

        let cancelled = false;
        (async () => {
            const entries = await Promise.all(missing.map(async (id) => {
                try {
                    const u = await getUserData(id);
                    const username = getDisplayName({
                        profile: u || null,
                        candidates: [u?.username, u?.displayName, u?.name]
                    });
                    const userProfession = u?.userProfession || u?.profession || '';
                    const photoURL = u?.photoURL || u?.userPhotoURL || '';
                    return [id, { username, userProfession, photoURL }];
                } catch {
                    return [id, { username: 'User', userProfession: '', photoURL: '' }];
                }
            }));

            if (!cancelled) {
                setUserCache(prev => {
                    const next = { ...prev };
                    entries.forEach(([id, profile]) => { next[id] = profile; });
                    return next;
                });
            }
        })();

        return () => { cancelled = true; };
    // Note: We use Object.keys(userCache).length instead of userCache to avoid infinite loop
    // since this effect updates userCache. Length-based dependency is sufficient because
    // we only need to re-run when new profiles are added to the cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredNotifications, invites, Object.keys(userCache).length]);

    // Follow request handlers
    const handleFollowRequestAccept = async (notification) => {
        try {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.add(notification.id);
                return next;
            });
            const result = await notificationActionHandler.handleFollowRequestAccept(
                notification,
                currentUser?.uid
            );

            if (!result?.success) {
                console.error('Failed to accept follow request notification:', result?.error);
            } else {
                dispatch(markAsReadAction([notification.id]));
                dispatch(updateNotification({ id: notification.id, requestStatus: 'accepted', read: true }));

                try {
                    const followerId = notification.actorId;
                    const prevFollowers = Array.isArray(authUserData?.followers) ? authUserData.followers : [];
                    const alreadyFollower = prevFollowers.includes(followerId);
                    const nextFollowers = alreadyFollower ? prevFollowers : [...prevFollowers, followerId];
                    const prevCount = typeof authUserData?.followersCount === 'number' ? authUserData.followersCount : prevFollowers.length;
                    const nextCount = alreadyFollower ? prevCount : prevCount + 1;
                    dispatch(updateUserDataLocal({ followers: nextFollowers, followersCount: nextCount }));
                } catch (_) { }
            }
        } catch (error) {
            console.error('Error accepting follow request notification:', error);
        } finally {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.delete(notification.id);
                return next;
            });
        }
    };

    const handleFollowRequestReject = async (notification) => {
        try {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.add(notification.id);
                return next;
            });
            const result = await notificationActionHandler.handleFollowRequestReject(
                notification,
                currentUser?.uid
            );

            if (!result?.success) {
                console.error('Failed to reject follow request notification:', result?.error);
            } else {
                dispatch(markAsReadAction([notification.id]));
                dispatch(updateNotification({ id: notification.id, requestStatus: 'rejected', read: true }));
            }
        } catch (error) {
            console.error('Error rejecting follow request notification:', error);
        } finally {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.delete(notification.id);
                return next;
            });
        }
    };

    // Project invite handlers
    const handleAccept = async (inviteId, notification) => {
        try {
            const result = await notificationActionHandler.handleProjectInviteAccept(
                inviteId,
                notification,
                navigate
            );
            if (result.success) console.log('Successfully joined project!');
        } catch (e) {
            console.error('Failed to accept invite:', e);
        }
    };

    const handleDecline = async (inviteId, notification) => {
        try {
            const result = await notificationActionHandler.handleProjectInviteDecline(
                inviteId,
                notification
            );
            if (result.success) console.log('Successfully declined invite');
        } catch (e) {
            console.error('Failed to decline invite:', e);
        }
    };

    const handleNotificationClick = async (notification) => {
        try {
            const result = await notificationActionHandler.handleNotificationClick(notification, navigate, { currentUserId: currentUser?.uid });
            if (!result.success) console.error('Failed to handle notification click:', result.error);
        } catch (error) {
            console.error('Error handling notification click:', error);
            if (!notification.read) {
                dispatch(markAsReadAction([notification.id]));
            }
        }
    };

    const combinedNotifications = useMemo(() => {
        const inviteAsNotif = invites.map(inv => ({
            id: inv.id,
            type: 'project_invite',
            actorId: inv.inviterId,
            projectId: inv.projectId,
            createdAt: inv.createdAt,
            read: false,
            title: `${userCache[inv.inviterId]?.username || 'Someone'} invited you to a project`,
            message: 'You have been invited to join a project',
            _isInvite: true,
            _raw: inv,
        }));

        return [...inviteAsNotif, ...filteredNotifications].sort((a, b) => {
            const aTime = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
            const bTime = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
            return bTime - aTime;
        });
    }, [invites, filteredNotifications, userCache]);

    const groupedNotifications = useMemo(() => {
        const groups = {};
        combinedNotifications.forEach(notification => {
            const group = getDateGroup(notification.createdAt);
            if (!groups[group]) groups[group] = [];
            groups[group].push(notification);
        });
        return groups;
    }, [combinedNotifications]);

    // Bulk selection helpers
    const toggleSelectionMode = () => {
        setSelectionMode(prev => !prev);
        setSelectedIds(new Set());
    };
    const toggleSelected = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const selectAllVisible = () => {
        setSelectedIds(new Set(combinedNotifications.filter(n => !n._isInvite).map(n => n.id)));
    };
    const clearSelection = () => setSelectedIds(new Set());
    const handleBulkMarkRead = () => {
        if (selectedIds.size === 0) return;
        markAsRead(Array.from(selectedIds));
        clearSelection();
        setSelectionMode(false);
    };
    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        for (const id of selectedIds) {
            await deleteNotification(id);
        }
        clearSelection();
        setSelectionMode(false);
    };

    const toggleMenu = (id) => setOpenMenuId(prev => prev === id ? null : id);
    const handleMarkRead = (notification, e) => {
        e.stopPropagation();
        if (!notification.read) markAsRead([notification.id]);
        setOpenMenuId(null);
    };
    const handleDeleteNotification = (notification, e) => {
        e.stopPropagation();
        deleteNotification(notification.id);
        setOpenMenuId(null);
    };

    return {
        userCache,
        selectionMode,
        selectedIds,
        processingIds,
        openMenuId,
        toggleMenu,
        handleMarkRead,
        handleDeleteNotification,
        handleFollowRequestAccept,
        handleFollowRequestReject,
        handleAccept,
        handleDecline,
        handleNotificationClick,
        combinedNotifications,
        groupedNotifications,
        toggleSelectionMode,
        toggleSelected,
        selectAllVisible,
        handleBulkMarkRead,
        handleBulkDelete
    };
};
